// src/pages/AboutUsPage.tsx

import { FC } from 'react';
import styles from '../css/AboutUsPage.module.css';
import { motion, Variants } from 'framer-motion';
import { Quote, Target } from 'lucide-react';

// Shared Components
import AnimatedSection from '../components/Shared/AnimatedSection';
import LeadershipProfileCard from '../components/AboutUs/LeadershipProfileCard';

// --- Images ---
import ProfileImg1 from '../assets/images/ProfileImage1.jpeg';
import ProfileImg2 from '../assets/images/ProfileImage2.jpeg';
import ProfileImg3 from '../assets/images/ProfileImage3.jpeg';
import ProfileImg4 from '../assets/images/ProfileImage4.jpeg';
import ProfileImg5 from '../assets/images/ProfileImage5.jpeg';

import KankamImg from '../assets/images/KankamProfile.jpeg';

// --- Data ---
const founderProfile = { 
    name: 'Atoapoma Asuama Yeboah', 
    title: 'Founder & CEO', 
    image: ProfileImg1, 
    linkedinUrl: 'https://www.linkedin.com/in/atoapoma-yeboah-asuama-3b6944158/',
    expertiseSummary: 'Chartered Accountant (ICAG) & Tax Practitioner. Architect of OTA ERP v1. Combines deep financial rigor with Full-Stack Engineering.',
    slug: '/profile/Atoapoma'
};

const strategicBoard = [
    { 
        name: 'Prof. Godfred Amewu', 
        title: 'Global Strategy Advisor', 
        image: ProfileImg2, 
        linkedinUrl: 'https://ugbs.ug.edu.gh/faculty/godfred-amewu',
        expertiseSummary: 'Associate Professor (UGBS). Leader in Impact Investing across Africa & Policy Advisor.',
        slug: '/profile/Amewu' 
    },
    { 
        name: 'Silas Adomako', 
        title: 'Finance & Startup Advisor', 
        image: ProfileImg3, 
        linkedinUrl: 'https://www.linkedin.com/in/silas-adomako/',
        expertiseSummary: 'Investment strategy and corporate finance governance.',
        slug: '/profile/Silas'
    },
    { 
        name: 'Nii Armah Addy Esq', 
        title: 'Non-Executive Director, Legal Affairs', 
        image: ProfileImg4, 
        linkedinUrl: 'https://www.linkedin.com/in/nii-armah-addy-19968b152/?originalSubdomain=gh',
        expertiseSummary: 'Lawyer & Chartered Management Consultant. Provides board-level oversight on corporate governance, legal strategy, and regulatory compliance.',
        slug: '/profile/Nii'
    },
    { 
        name: '.....', 
        title: '.....', 
        image: ProfileImg5, 
        linkedinUrl: '#',
        expertiseSummary: '.....' 
    },
];

const coreTeam = [
    { 
        name: 'Kankam Abrampa Yeboah', 
        title: 'Lead DevOps Engineer', 
        image: KankamImg, 
        linkedinUrl: 'https://www.linkedin.com/in/kankamyeboah/',
        expertiseSummary: 'AWS Certified Solutions Architect & DevSecOps Specialist. Orchestrating cloud infrastructure, Kubernetes deployments, and CI/CD pipelines.',
        slug: '/profile/kankam' // <--- This makes the card clickable
    },

];


const AboutUsPage: FC = () => {
    
    const containerVariants: Variants = { 
        visible: { transition: { staggerChildren: 0.1, delayChildren: 0.1 } },
    };

    const itemVariants: Variants = { 
        hidden: { opacity: 0, y: 20, filter: 'blur(2px)' },
        visible: { opacity: 1, y: 0, filter: 'blur(0px)', transition: { duration: 0.6, ease: "easeOut" } },
    };

    return (
        <div className={styles.aboutUsPage}>

            {/* 1. HERO SECTION */}
            <section className={styles.heroSection}>
                <div className={styles.dotBackground}></div>
                <div className={styles.container}>
                    <div className={styles.heroContent}>
                        <motion.div 
                            initial="hidden" animate="visible" variants={containerVariants}
                            className={styles.heroWrapper}
                        >
                            <motion.span variants={itemVariants} className={styles.heroBadge}>
                                Corporate Ethos
                            </motion.span>
                            
                            <motion.h1 variants={itemVariants} className={styles.heroTitle}>
                                Defining the <br/>
                                <span className={styles.titleHighlight}>Business Logic</span> of <br/>
                                the Future.
                            </motion.h1>
                            
                            <motion.p variants={itemVariants} className={styles.heroSubtitle}>
                                OTA TECH Inc. bridges the gap between rigid legacy systems and fluid modern operations. 
                                We are a team of domain experts who live the problems we solve.
                            </motion.p>
                        </motion.div>
                    </div>
                </div>
                <div className={styles.heroFade}></div>
            </section>

            {/* 2. NARRATIVE SECTION */}
            <AnimatedSection className={styles.narrativeSection}>
                <div className={styles.container}>
                    <div className={styles.storyGrid}>
                        
                        <motion.div className={styles.storyLeft} variants={itemVariants}>
                            <span className={styles.sectionLabel}>Our Origins</span>
                            <h2 className={styles.sectionTitle}>The Friction Point</h2>
                            <div className={styles.prose}>
                                <p>
                                    OTA TECH Inc. was established in 2023 with a singular objective: 
                                    <span className={styles.textHighlight}> to eliminate the operational friction created by fragmented business software.</span>
                                </p>
                                <p>
                                    The founder, <strong>Ato Yeboah</strong>, recognized that the most effective solutions come not from career developers alone, but from professionals who face operational, financial, and compliance challenges daily.
                                </p>
                                <p>
                                    We operate on a <strong>Venture Studio model</strong>. Our flagship, OTA ERP, acts as the anchor for a portfolio of specialized FinTech and LegalTech solutions. We are US-registered with a global, remote-first engineering team.
                                </p>
                            </div>
                        </motion.div>

                        <motion.div className={styles.missionBox} variants={itemVariants}>
                            <div className={styles.missionHeader}>
                                <Target size={24} className={styles.missionIcon} />
                                <span className={styles.missionLabel}>Core Directive</span>
                            </div>
                            <div className={styles.quoteWrapper}>
                                <Quote size={40} className={styles.quoteIcon} />
                                <p className={styles.missionText}>
                                    To engineer intelligent, unified, and scalable software platforms that grant enterprises unprecedented clarity and operational agility.
                                </p>
                            </div>
                            <div className={styles.missionFooter}>
                                <div className={styles.missionLine}></div>
                                <span>Est. 2024</span>
                            </div>
                        </motion.div>

                    </div>
                </div>
            </AnimatedSection>

            {/* 3. FOUNDER SPOTLIGHT */}
            <AnimatedSection className={styles.founderSection}>
                <div className={styles.container}>
                    <div className={styles.sectionHeaderCenter}>
                        <span className={styles.sectionLabel}>Leadership</span>
                        <h2 className={styles.sectionTitle}>The Duality of Expertise</h2>
                        <p className={styles.sectionSubtitle}>
                            Leadership that speaks the language of Finance and the logic of Code.
                        </p>
                    </div>

                    <div className={styles.founderWrapper}>
                        <LeadershipProfileCard {...founderProfile} variants={itemVariants} />
                    </div>
                </div>
            </AnimatedSection>

            {/* 4. GOVERNANCE BOARD */}
            <AnimatedSection className={styles.boardSection}>
                <div className={styles.container}>
                    <div className={styles.sectionHeaderCenter}>
                         <span className={styles.sectionLabel}>Governance</span>
                        <h2 className={styles.sectionTitle}>Strategic Board</h2>
                        <p className={styles.sectionSubtitle}>
                            Guided by a council of industry veterans ensuring global compliance and scalability.
                        </p>
                    </div>
                    
                    <motion.div className={styles.leadershipGrid} variants={containerVariants}>
                        {strategicBoard.map((member, index) => (
                            <LeadershipProfileCard key={index} {...member} variants={itemVariants} />
                        ))}
                    </motion.div>
                </div>
            </AnimatedSection>

            {/* 5. ENGINEERING & CORE TEAM */}
            <AnimatedSection className={styles.teamSection}>
                <div className={styles.container}>
                    <div className={styles.sectionHeaderCenter}>
                        <span className={styles.sectionLabel}>The Builders</span>
                        <h2 className={styles.sectionTitle}>Engineering & Operations</h2>
                        <p className={styles.sectionSubtitle}>
                            The technical architects turning complex requirements into elegant, cloud-native solutions.
                        </p>
                    </div>
                    
                    {/* Reusing leadershipGrid style to ensure it looks EXACTLY like the board */}
                    <motion.div className={styles.leadershipGrid} variants={containerVariants}>
                        {coreTeam.map((member, index) => (
                            <LeadershipProfileCard key={index} {...member} variants={itemVariants} />
                        ))}
                    </motion.div>
                </div>
            </AnimatedSection>

        </div>
    );
};

export default AboutUsPage;